import React, { useMemo, useState } from 'react';
import { getTodayDate } from '../../utils/dateUtils';
import axios from 'axios';
import { AlertTriangle } from 'lucide-react';
import {
  ModalShell, Body, FieldRow, Field, Label, Input, Footer, Button, ErrorBanner
} from '../realestate/reModalShared';
import type { CloEquityPositionLite } from './CloEquityMtmModal';

import { DateField } from '../shared/DateField';

interface Props {
  position: CloEquityPositionLite;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Disposition (full or partial — F-CLO-035) of a posted CLO tranche position.
 * Posts to {@code POST /api/credit/clo-equity/{securityId}/dispose}. The
 * operator enters Face to Sell + Price (per 100); Proceeds is two-way bound
 * (editing it re-derives the price). Booking requires a second, armed click
 * that restates the implied realized P&L — a fat-fingered proceeds number can
 * no longer book a silent multi-million loss.
 */
const CloEquityDisposeModal: React.FC<Props> = ({ position, onClose, onSuccess }) => {
  const heldFace = position.quantity || 0;
  const costBasis = position.investmentAmount || 0;
  const impliedMvPrice = heldFace > 0 && position.marketValue
    ? (position.marketValue * 100) / heldFace : 100;

  const [saleDate, setSaleDate] = useState<string>(getTodayDate());
  const [face, setFace] = useState<string>(heldFace ? String(heldFace) : '');
  const [price, setPrice] = useState<string>(impliedMvPrice.toFixed(4));
  const [proceeds, setProceeds] = useState<string>(
    heldFace ? ((heldFace * impliedMvPrice) / 100).toFixed(2) : '');
  const [armed, setArmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const faceNum = Number(face) || 0;
  const proceedsNum = Number(proceeds) || 0;
  const isPartial = heldFace > 0 && faceNum > 0 && faceNum < heldFace;
  const fraction = heldFace > 0 ? faceNum / heldFace : 1;

  // Implied realized P&L = proceeds − cost basis relieved pro-rata.
  const realisedGain = useMemo(
    () => proceedsNum - costBasis * fraction,
    [proceedsNum, costBasis, fraction]);

  const disarm = () => setArmed(false);

  const onFaceChange = (v: string) => {
    setFace(v);
    const f = Number(v) || 0;
    const p = Number(price) || 0;
    setProceeds(((f * p) / 100).toFixed(2));
    disarm();
  };
  const onPriceChange = (v: string) => {
    setPrice(v);
    const p = Number(v) || 0;
    setProceeds(((faceNum * p) / 100).toFixed(2));
    disarm();
  };
  const onProceedsChange = (v: string) => {
    setProceeds(v);
    const pr = Number(v) || 0;
    if (faceNum > 0) setPrice(((pr * 100) / faceNum).toFixed(4));
    disarm();
  };

  const validate = (): string | null => {
    if (!faceNum || faceNum <= 0) return 'Face to sell must be > 0';
    if (heldFace > 0 && faceNum > heldFace)
      return `Face to sell exceeds held face (${heldFace.toLocaleString()})`;
    if (proceedsNum < 0) return 'Proceeds must be >= 0';
    return null;
  };

  const submit = async () => {
    setError(null);
    const v = validate();
    if (v) { setError(v); return; }
    if (!armed) { setArmed(true); return; }
    setSubmitting(true);
    try {
      await axios.post(`/api/credit/clo-equity/${position.securityId}/dispose`, {
        quantity: faceNum,
        proceeds: proceedsNum,
        saleDate,
        // Partial keys carry the face so two same-day partials don't collide
        // on uq_trade_tx_source_ref (mirrors the backend default key).
        idempotencyKey: `dispose-${position.securityId}-${saleDate}` +
          (isPartial ? `-q${faceNum}` : ''),
      });
      onSuccess();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to record disposition';
      setError(msg);
      setArmed(false);
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n: number, d = 0) =>
    n.toLocaleString(undefined, { maximumFractionDigits: d });

  return (
    <ModalShell
      title="Record Disposition"
      subtitle={`${position.dealName || 'CLO Equity'}${position.trancheName ? ' • ' + position.trancheName : ''}${position.portfolioName ? ' • ' + position.portfolioName : ''}`}
      icon={<AlertTriangle size={18} className="text-red-400" />}
      onClose={onClose}
    >
      <Body>
        <div style={{
          background: 'rgba(239, 68, 68, 0.06)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          fontSize: '0.8rem',
          color: '#fca5a5'
        }}>
          This emits a <b>DISPOSE</b> trade through the posting engine
          {isPartial
            ? <> and reduces the position by the face sold (partial dispose).</>
            : <> and reduces <code>fact_positions</code> to zero for this position.</>}
          {' '}Realized P&L is computed from proceeds − cost basis on each lot.
          Reversal requires a linked reversal trade.
        </div>

        <FieldRow>
          <Field>
            <Label>Face to Sell{heldFace > 0 ? ` (held: ${fmt(heldFace)})` : ''}</Label>
            <Input type="number" step="0.01" value={face}
              onChange={e => onFaceChange(e.target.value)} />
          </Field>
          <Field>
            <Label>Price (per 100)</Label>
            <Input type="number" step="0.0001" value={price}
              onChange={e => onPriceChange(e.target.value)} />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <Label>Sale Date</Label>
            <DateField
              value={saleDate ?? ''}
              onChange={iso => { setSaleDate(iso); disarm(); }}
            />
          </Field>
          <Field>
            <Label>Gross Proceeds (USD)</Label>
            <Input type="number" step="0.01" value={proceeds}
              onChange={e => onProceedsChange(e.target.value)} />
          </Field>
        </FieldRow>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
          <Field>
            <Label>Cost Basis Relieved</Label>
            <div style={{ color: '#94a3b8', fontSize: '0.95rem', fontWeight: 600 }}>
              ${fmt(costBasis * fraction)}{isPartial ? ` (${(fraction * 100).toFixed(1)}%)` : ''}
            </div>
          </Field>
          <Field>
            <Label>Last MV</Label>
            <div style={{ color: '#60a5fa', fontSize: '0.95rem', fontWeight: 600 }}>
              ${fmt(position.marketValue || 0)}
            </div>
          </Field>
          <Field>
            <Label>Implied Realised Gain / (Loss)</Label>
            <div style={{
              color: realisedGain >= 0 ? '#34d399' : '#f87171',
              fontSize: '0.95rem',
              fontWeight: 700
            }}>
              {realisedGain >= 0 ? '+' : ''}${fmt(realisedGain)}
            </div>
          </Field>
        </div>

        {armed && (
          <div style={{
            background: 'rgba(245, 158, 11, 0.08)',
            border: '1px solid rgba(245, 158, 11, 0.35)',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            fontSize: '0.85rem',
            color: '#fcd34d',
            fontWeight: 600
          }}>
            Confirm: selling {isPartial ? `${(fraction * 100).toFixed(1)}% of` : 'the ENTIRE'} position
            — face ${fmt(faceNum)} @ {Number(price).toFixed(4)} for ${fmt(proceedsNum)} proceeds,
            booking an implied realized {realisedGain >= 0 ? 'gain' : 'LOSS'} of ${fmt(Math.abs(realisedGain))}.
            Click again to book.
          </div>
        )}

        {error && <ErrorBanner>{error}</ErrorBanner>}
      </Body>

      <Footer>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button $danger onClick={submit} disabled={submitting}>
          {submitting ? 'Disposing…'
            : armed ? `Confirm — book ${realisedGain >= 0 ? 'gain' : 'loss'} of $${fmt(Math.abs(realisedGain))}`
            : 'Review Disposition'}
        </Button>
      </Footer>
    </ModalShell>
  );
};

export default CloEquityDisposeModal;
